import { CompactText } from 'ui/CompactText';
import { FileDropZone } from 'ui/FileDrop/FileDropZone';
import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';

import { prepareImageFile } from 'utils/prepareImageFile';
import { blurWarningMessage, estimateImageSharpness } from 'utils/imageSharpness';

import { DocumentCamera } from '../camera/DocumentCamera';
import type { CaptureStepConfig, CapturedImage, ImageSource } from '../types';

import classes from '../PassportPage.module.scss';

type ReactNode = import("react").ReactNode;
interface CaptureStepProps {
  config: CaptureStepConfig;
  onConfirm: (image: CapturedImage) => void;
  onBack: () => void;
  active: boolean;
  showBack?: boolean;
  /** Сообщение модератора/пересъёмки: показываем под заголовком, а не над шапкой страницы. */
  notice?: ReactNode;
}

const GALLERY_ACCEPT =
  'image/jpeg,image/png,image/heic,image/heif,image/webp,.jpg,.jpeg,.png,.heic,.heif,.webp';

const STEP_COPY: Record<
  CaptureStepConfig['id'],
  { title: string; lead: string; check: string }
> = {
  spread: {
    title: 'Разворот паспорта',
    lead: 'Покажите разворот целиком. Все четыре угла должны быть в кадре, текст - читаемым. Не закрывайте фотографию и данные.',
    check: 'Проверьте снимок перед переходом к следующему шагу.',
  },
  registration: {
    title: 'Страница регистрации',
    lead: 'Покажите страницу регистрации целиком. Углы в кадре, текст читается, без блика на штампе.',
    check: 'Проверьте снимок перед переходом к следующему шагу.',
  },
  selfie: {
    title: 'Селфи с паспортом',
    lead: 'Держите разворот рядом с лицом. Лицо и данные должны быть в кадре и читаться.',
    check: 'Проверьте снимок перед переходом к следующему шагу.',
  },
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export const CaptureStep = ({
  config,
  onConfirm,
  onBack,
  active,
  notice,
}: CaptureStepProps) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [preview, setPreview] = useState<CapturedImage | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [blurWarning, setBlurWarning] = useState<string | null>(null);
  const [checkingBlur, setCheckingBlur] = useState(false);
  const copy = STEP_COPY[config.id];

  const handleCapture = useCallback(
    async (blob: Blob, source: ImageSource) => {
      const dataUrl = await blobToDataUrl(blob);
      setFileError(null);
      setBlurWarning(null);
      setCameraOpen(false);
      setPreview({
        id: config.id,
        blob,
        dataUrl,
        source,
      });
    },
    [config.id],
  );

  useEffect(() => {
    if (!preview?.blob) {
      setBlurWarning(null);
      return;
    }
    let cancelled = false;
    setCheckingBlur(true);
    void estimateImageSharpness(preview.blob)
      .then((score) => {
        if (!cancelled) setBlurWarning(blurWarningMessage(score));
      })
      .catch(() => {
        if (!cancelled) setBlurWarning(null);
      })
      .finally(() => {
        if (!cancelled) setCheckingBlur(false);
      });
    return () => {
      cancelled = true;
    };
  }, [preview]);

  const takeGalleryFile = async (file: File) => {
    setFileError(null);
    try {
      const prepared = await prepareImageFile(file, { documentMode: true });
      await handleCapture(prepared, 'gallery');
    } catch (err) {
      setFileError(
        err instanceof Error
          ? err.message
          : 'Выберите файл JPEG, PNG или HEIC. Другие форматы не принимаются.',
      );
    }
  };

  const handleGallery = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    await takeGalleryFile(file);
  };

  if (cameraOpen) {
    return (
      <DocumentCamera
        config={config}
        onCapture={(blob, source) => void handleCapture(blob, source)}
        onBack={() => setCameraOpen(false)}
        active={active}
      />
    );
  }

  return (
    <main className={classes.page}>
      <p className={classes.crumb}>Личный кабинет / Проверка документов</p>
      <h1 className={classes.title}>{copy.title}</h1>
      {notice}
      <FileDropZone
        className={classes.card}
        accept={GALLERY_ACCEPT}
        hint="Отпустите фото, чтобы загрузить"
        onFiles={(files) => {
          const file = files[0];
          if (file) void takeGalleryFile(file);
        }}
      >
        <p className={classes.stepTitle}>Шаг {config.stepNumber} из 3</p>
        <div className={classes.progress}>
          {(['spread', 'registration', 'selfie'] as const).map((id, index) => {
            const on = config.stepNumber === index + 1;
            const labels = ['1. Разворот', '2. Регистрация', '3. Селфи'];
            return (
              <div key={id} className={classes.progressItem}>
                <div className={on ? classes.progressBarOn : classes.progressBar} />
                <p className={on ? classes.progressLabelOn : classes.progressLabel}>{labels[index]}</p>
              </div>
            );
          })}
        </div>
        <div className={classes.layout}>
          <div className={classes.preview}>
            {preview ? (
              <img src={preview.dataUrl} alt={copy.title} className={classes.previewImage} />
            ) : (
              <span className={classes.mark} aria-hidden>
                +
              </span>
            )}
            <p className={classes.previewTitle}>{copy.title}</p>
            <p className={classes.previewText}>
              {preview ? 'Снимок выбран. Можно переснять или идти дальше.' : 'Предпросмотр появится после выбора фото.'}
            </p>
            <p className={classes.previewText}>
              {preview ? (preview.source === 'camera' ? 'Снято камерой' : 'Загружено из файла') : 'Фото пока не выбрано'}
            </p>
          </div>
          <div>
            <p className={classes.sideTitle}>Подготовьте фото</p>
            <p className={classes.sideText}>{copy.lead}</p>
            <CompactText className={classes.note} tone={'info'}>{copy.check}</CompactText>
          </div>
        </div>
        {checkingBlur ? <CompactText className={classes.hint} tone={'info'}>Проверяем читаемость...</CompactText> : null}
        {blurWarning ? (
          <p className={classes.hint} role='status'>
            {blurWarning}
          </p>
        ) : null}
        {fileError ? (
          <p className={classes.error} role='alert'>
            {fileError}
          </p>
        ) : null}
        <div className={classes.actions}>
          <button type='button' className={classes.primary} onClick={() => fileRef.current?.click()}>
            Выбрать фото
          </button>
          <button type='button' className={classes.secondary} onClick={() => setCameraOpen(true)}>
            Открыть камеру
          </button>
          {preview ? (
            <button type='button' className={classes.secondary} onClick={() => setPreview(null)}>
              Переснять
            </button>
          ) : null}
        </div>
        <CompactText className={classes.note} tone={'info'}>
          Документы используются для проверки профиля. Не публикуйте паспортные данные в описании или чате.
        </CompactText>
        <div className={classes.nav}>
          <button type='button' className={classes.secondary} onClick={onBack}>
            Назад
          </button>
          <button
            type='button'
            className={classes.primary}
            disabled={!preview}
            onClick={() => preview && onConfirm(preview)}
          >
            Далее
          </button>
        </div>
        <input
          ref={fileRef}
          type='file'
          accept={GALLERY_ACCEPT}
          className={classes.hiddenInput}
          onChange={(event) => void handleGallery(event)}
        />
      </FileDropZone>
    </main>
  );
};
